import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { authFetch } from '../../utils/authFetch';

// Import legacy action types for extraReducers bridge
import {
  SET_RISK_EVALUATION,
  UPDATE_RISK_EVALUATION,
  FETCH_RISK_EVALUATIONS,
  FETCH_RISK_EVALUATIONS_SUCCESS,
  FETCH_RISK_EVALUATIONS_FAILURE,
} from '../actions/actionTypes.js';

// ============================================================================
// Async Thunks for Explorer Evaluations
// ============================================================================

/**
 * Save explorer risk evaluation to database
 * Replaces direct HTTP calls in ExplorerEvaluationService
 */
export const saveExplorerEvaluation = createAsyncThunk(
  'riskEvaluations/saveExplorerEvaluation',
  async ({ modelUuid, assessmentUuid, riskId, riskName, themeId, riskArea, priority, rationale, data }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveRiskEvaluation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid,
          assessmentUuid,
          riskName,
          riskId,
          themeId,
          riskArea,
          priority,
          rationale,
          data: data || {},
          assessmentType: 'explorer'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      return {
        assessmentUuid,
        riskId,
        riskName,
        themeId,
        riskArea,
        priority,
        rationale,
        data,
        success: true,
        message: result.message
      };
    } catch (error) {
      return rejectWithValue({
        assessmentUuid,
        riskId,
        error: error.message
      });
    }
  }
);

/**
 * Fetch explorer evaluations from database for hydration
 */
export const fetchExplorerEvaluations = createAsyncThunk(
  'riskEvaluations/fetchExplorerEvaluations',
  async ({ modelUuid, assessmentUuid }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/fetchRiskEvaluations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid,
          assessmentUuid,
          assessmentType: 'explorer'
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      return {
        assessmentUuid,
        evaluations: result.evaluations || result.data?.rows || []
      };
    } catch (error) {
      return rejectWithValue({
        assessmentUuid,
        error: error.message
      });
    }
  }
);

// ============================================================================
// Risk Evaluations Slice
// Manages risk evaluation data (impact, likelihood, priority) for assessments
// ============================================================================

/**
 * State Shape:
 * {
 *   byRisk: {
 *     [riskId]: {
 *       [themeId]: {
 *         riskImpact: { overallImpact, intensity, ... },
 *         likelihood: { probability, frequency, ... },
 *         ...other evaluation attributes
 *       }
 *     }
 *   },
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [sectionName]: {
 *         [category]: {
 *           [riskId]: { evaluationData, priority, rationale }
 *         }
 *       }
 *     }
 *   },
 *   loading: boolean,
 *   error: string | null
 * }
 */

const initialState = {
  byRisk: {},
  byAssessment: {},
  loading: false,
  error: null,
};

// Helper to ensure nested structure exists
const ensureStructure = (state, assessmentUuid, sectionName, category, riskId) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = {};
  }
  if (!state.byAssessment[assessmentUuid][sectionName]) {
    state.byAssessment[assessmentUuid][sectionName] = {};
  }
  if (!state.byAssessment[assessmentUuid][sectionName][category]) {
    state.byAssessment[assessmentUuid][sectionName][category] = {};
  }
  if (riskId && !state.byAssessment[assessmentUuid][sectionName][category][riskId]) {
    state.byAssessment[assessmentUuid][sectionName][category][riskId] = {
      evaluationData: {},
    };
  }
};

const riskEvaluationsSlice = createSlice({
  name: 'riskEvaluations',
  initialState,
  reducers: {
    // RTK action creators for new code

    setRiskEvaluation: (state, action) => {
      const { assessmentUuid, riskId, themeId, evaluationData, sectionName, category } =
        action.payload;
      ensureStructure(state, assessmentUuid, sectionName, category, riskId);

      const riskData = state.byAssessment[assessmentUuid][sectionName][category][riskId];
      if (!riskData.evaluationData) {
        riskData.evaluationData = {};
      }
      if (!riskData.evaluationData[themeId]) {
        riskData.evaluationData[themeId] = {};
      }

      // Deep merge evaluation data
      riskData.evaluationData[themeId] = _.merge(
        {},
        riskData.evaluationData[themeId],
        evaluationData
      );

      // Also update byRisk for direct lookups
      if (!state.byRisk[riskId]) {
        state.byRisk[riskId] = {};
      }
      state.byRisk[riskId][themeId] = _.merge(
        {},
        state.byRisk[riskId][themeId] || {},
        evaluationData
      );
    },

    updateRiskEvaluation: (state, action) => {
      const {
        assessmentUuid,
        riskId,
        sectionName,
        themeId,
        attribute,
        property,
        value,
        priority,
        rationale,
        category,
      } = action.payload;

      ensureStructure(state, assessmentUuid, sectionName, category, riskId);
      const riskData = state.byAssessment[assessmentUuid][sectionName][category][riskId];

      if (!riskData.evaluationData) {
        riskData.evaluationData = {};
      }
      if (!riskData.evaluationData[themeId]) {
        riskData.evaluationData[themeId] = {};
      }
      if (!riskData.evaluationData[themeId][attribute]) {
        riskData.evaluationData[themeId][attribute] = {};
      }

      // Set the property on the attribute
      riskData.evaluationData[themeId][attribute][property] = value;

      // Update priority/rationale if provided
      if (priority !== undefined) {
        riskData.priority = priority;
      }
      if (rationale !== undefined) {
        riskData.rationale = rationale;
      }

      // Also update byRisk
      if (!state.byRisk[riskId]) {
        state.byRisk[riskId] = {};
      }
      if (!state.byRisk[riskId][themeId]) {
        state.byRisk[riskId][themeId] = {};
      }
      if (!state.byRisk[riskId][themeId][attribute]) {
        state.byRisk[riskId][themeId][attribute] = {};
      }
      state.byRisk[riskId][themeId][attribute][property] = value;
    },

    // Clear evaluations for an assessment
    clearAssessmentEvaluations: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },

    // Clear evaluations for a specific risk
    clearRiskEvaluations: (state, action) => {
      const { riskId } = action.payload;
      delete state.byRisk[riskId];
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // ============================================================================
    // Explorer-Specific Reducers
    // Explorer evaluations use 'explorer' as a namespace to distinguish from Builder
    // ============================================================================

    // Set Explorer risk evaluation
    setExplorerRiskEvaluation: (state, action) => {
      const { assessmentUuid, riskId, areaKey, category, evaluationData, priority, rationale, impactFactors } =
        action.payload;

      // Ensure structure: byAssessment[assessmentUuid].explorer[areaKey][category][riskId]
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = { explorer: {} };
      }
      if (!state.byAssessment[assessmentUuid].explorer) {
        state.byAssessment[assessmentUuid].explorer = {};
      }
      if (!state.byAssessment[assessmentUuid].explorer[areaKey]) {
        state.byAssessment[assessmentUuid].explorer[areaKey] = {};
      }
      if (!state.byAssessment[assessmentUuid].explorer[areaKey][category]) {
        state.byAssessment[assessmentUuid].explorer[areaKey][category] = {};
      }

      state.byAssessment[assessmentUuid].explorer[areaKey][category][riskId] = {
        evaluationData: evaluationData || {},
        priority,
        rationale,
        impactFactors: impactFactors || [],
      };

      // Also update byRisk for direct lookups (Explorer uses 'explorer' as themeId namespace)
      if (!state.byRisk[riskId]) {
        state.byRisk[riskId] = {};
      }
      state.byRisk[riskId].explorer = {
        ...state.byRisk[riskId].explorer,
        ...evaluationData,
        priority,
        rationale,
        impactFactors,
      };
    },

    // Update Explorer risk evaluation field
    updateExplorerRiskEvaluation: (state, action) => {
      const { assessmentUuid, riskId, areaKey, category, updates } = action.payload;

      // Ensure structure exists
      const explorerPath = state.byAssessment[assessmentUuid]?.explorer?.[areaKey]?.[category];
      if (!explorerPath || !explorerPath[riskId]) return;

      // Merge updates
      state.byAssessment[assessmentUuid].explorer[areaKey][category][riskId] = {
        ...explorerPath[riskId],
        ...updates,
      };

      // Also update byRisk
      if (state.byRisk[riskId]?.explorer) {
        state.byRisk[riskId].explorer = {
          ...state.byRisk[riskId].explorer,
          ...updates,
        };
      }
    },

    // Clear Explorer evaluations for an assessment
    clearExplorerEvaluations: (state, action) => {
      const { assessmentUuid } = action.payload;
      if (state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid].explorer = {};
      }
    },

    // Clear Explorer evaluations for a specific area
    clearExplorerAreaEvaluations: (state, action) => {
      const { assessmentUuid, areaKey } = action.payload;
      if (state.byAssessment[assessmentUuid]?.explorer) {
        delete state.byAssessment[assessmentUuid].explorer[areaKey];
      }
    },
  },

  extraReducers: (builder) => {
    // ============================================================================
    // Legacy Action Type Bridge
    // ============================================================================

    builder.addCase(SET_RISK_EVALUATION, (state, action) => {
      const { assessmentUuid, riskId, themeId, evaluationData, sectionName, category } =
        action.payload;
      ensureStructure(state, assessmentUuid, sectionName, category, riskId);

      const riskData = state.byAssessment[assessmentUuid][sectionName][category][riskId];
      if (!riskData.evaluationData) {
        riskData.evaluationData = {};
      }
      if (!riskData.evaluationData[themeId]) {
        riskData.evaluationData[themeId] = {};
      }

      riskData.evaluationData[themeId] = _.merge(
        {},
        riskData.evaluationData[themeId],
        evaluationData
      );

      if (!state.byRisk[riskId]) {
        state.byRisk[riskId] = {};
      }
      state.byRisk[riskId][themeId] = _.merge(
        {},
        state.byRisk[riskId][themeId] || {},
        evaluationData
      );
    });

    builder.addCase(UPDATE_RISK_EVALUATION, (state, action) => {
      const {
        assessmentUuid,
        riskId,
        sectionName,
        themeId,
        attribute,
        property,
        value,
        priority,
        rationale,
        category,
      } = action.payload;

      ensureStructure(state, assessmentUuid, sectionName, category, riskId);
      const riskData = state.byAssessment[assessmentUuid][sectionName][category][riskId];

      if (!riskData.evaluationData) {
        riskData.evaluationData = {};
      }
      if (!riskData.evaluationData[themeId]) {
        riskData.evaluationData[themeId] = {};
      }
      if (!riskData.evaluationData[themeId][attribute]) {
        riskData.evaluationData[themeId][attribute] = {};
      }

      riskData.evaluationData[themeId][attribute][property] = value;

      if (priority !== undefined) {
        riskData.priority = priority;
      }
      if (rationale !== undefined) {
        riskData.rationale = rationale;
      }

      if (!state.byRisk[riskId]) {
        state.byRisk[riskId] = {};
      }
      if (!state.byRisk[riskId][themeId]) {
        state.byRisk[riskId][themeId] = {};
      }
      if (!state.byRisk[riskId][themeId][attribute]) {
        state.byRisk[riskId][themeId][attribute] = {};
      }
      state.byRisk[riskId][themeId][attribute][property] = value;
    });

    // Fetch evaluations async flow
    builder
      .addCase(FETCH_RISK_EVALUATIONS, (state, action) => {
        const { assessmentUuid } = action.payload;
        state.loading = true;
        state.error = null;
        // Ensure structure exists
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
      })
      .addCase(FETCH_RISK_EVALUATIONS_SUCCESS, (state, action) => {
        const { assessmentUuid, evaluations, sectionName, riskEvaluationForm } = action.payload;
        state.loading = false;

        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
        if (!state.byAssessment[assessmentUuid][sectionName]) {
          state.byAssessment[assessmentUuid][sectionName] = {};
        }

        // Process evaluations from DB
        evaluations.forEach((dbEval) => {
          const riskId = dbEval.risk_id;
          const themeId = dbEval.theme_id;

          // Find or create category structure
          // Since we don't know the category from DB, store under a special key
          const category = dbEval.category || '_evaluations';

          ensureStructure(state, assessmentUuid, sectionName, category, riskId);
          const riskData = state.byAssessment[assessmentUuid][sectionName][category][riskId];

          if (!riskData.evaluationData) {
            riskData.evaluationData = {};
          }
          if (!riskData.evaluationData[themeId]) {
            riskData.evaluationData[themeId] = {};
          }

          // Use evaluation form from action payload if available
          const evaluationFormFromConfigs = riskEvaluationForm || [];

          if (evaluationFormFromConfigs.length > 0) {
            evaluationFormFromConfigs.forEach((criterion) => {
              const attr = criterion.attribute;
              if (!riskData.evaluationData[themeId][attr]) {
                riskData.evaluationData[themeId][attr] = {};
              }

              // baseEval
              const baseKey = criterion.baseEvaluation.name;
              riskData.evaluationData[themeId][attr][baseKey] = dbEval[baseKey] || '';

              // deepDive
              criterion.deepDive.forEach((field) => {
                riskData.evaluationData[themeId][attr][field.name] = dbEval[field.name] || '';
              });
            });
          }

          // Also update byRisk
          if (!state.byRisk[riskId]) {
            state.byRisk[riskId] = {};
          }
          state.byRisk[riskId][themeId] = _.cloneDeep(riskData.evaluationData[themeId]);
        });
      })
      .addCase(FETCH_RISK_EVALUATIONS_FAILURE, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to fetch evaluations';
      });

    // ============================================================================
    // Explorer Evaluation Thunks
    // ============================================================================

    // Save explorer evaluation
    builder
      .addCase(saveExplorerEvaluation.pending, (state, action) => {
        const { assessmentUuid, riskId } = action.meta.arg;
        // Track saving state per risk
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = { explorer: {} };
        }
        if (!state.byAssessment[assessmentUuid].explorerSaving) {
          state.byAssessment[assessmentUuid].explorerSaving = {};
        }
        state.byAssessment[assessmentUuid].explorerSaving[riskId] = true;
      })
      .addCase(saveExplorerEvaluation.fulfilled, (state, action) => {
        const { assessmentUuid, riskId, riskArea, priority, rationale } = action.payload;

        // Clear saving state
        if (state.byAssessment[assessmentUuid]?.explorerSaving) {
          state.byAssessment[assessmentUuid].explorerSaving[riskId] = false;
        }

        // Update explorer evaluation state with saved data
        if (!state.byAssessment[assessmentUuid].explorer) {
          state.byAssessment[assessmentUuid].explorer = {};
        }
        if (!state.byAssessment[assessmentUuid].explorer[riskArea]) {
          state.byAssessment[assessmentUuid].explorer[riskArea] = {};
        }
        if (!state.byAssessment[assessmentUuid].explorer[riskArea]._saved) {
          state.byAssessment[assessmentUuid].explorer[riskArea]._saved = {};
        }

        // Mark as saved with priority
        state.byAssessment[assessmentUuid].explorer[riskArea]._saved[riskId] = {
          priority,
          rationale,
          savedAt: new Date().toISOString()
        };
      })
      .addCase(saveExplorerEvaluation.rejected, (state, action) => {
        const { assessmentUuid, riskId, error } = action.payload || action.meta.arg;

        // Clear saving state and set error
        if (state.byAssessment[assessmentUuid]?.explorerSaving) {
          state.byAssessment[assessmentUuid].explorerSaving[riskId] = false;
        }
        if (!state.byAssessment[assessmentUuid].explorerErrors) {
          state.byAssessment[assessmentUuid].explorerErrors = {};
        }
        state.byAssessment[assessmentUuid].explorerErrors[riskId] = error || action.error?.message;
      });

    // Fetch explorer evaluations
    builder
      .addCase(fetchExplorerEvaluations.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = { explorer: {} };
        }
        state.byAssessment[assessmentUuid].explorerLoading = true;
        state.byAssessment[assessmentUuid].explorerError = null;
      })
      .addCase(fetchExplorerEvaluations.fulfilled, (state, action) => {
        const { assessmentUuid, evaluations } = action.payload;

        state.byAssessment[assessmentUuid].explorerLoading = false;

        // Process evaluations into explorer structure
        if (!state.byAssessment[assessmentUuid].explorer) {
          state.byAssessment[assessmentUuid].explorer = {};
        }

        evaluations.forEach((dbEval) => {
          const riskId = dbEval.risk_id;
          const riskArea = dbEval.risk_area || '_unknown';
          const category = dbEval.category || '_evaluations';

          if (!state.byAssessment[assessmentUuid].explorer[riskArea]) {
            state.byAssessment[assessmentUuid].explorer[riskArea] = {};
          }
          if (!state.byAssessment[assessmentUuid].explorer[riskArea][category]) {
            state.byAssessment[assessmentUuid].explorer[riskArea][category] = {};
          }

          state.byAssessment[assessmentUuid].explorer[riskArea][category][riskId] = {
            priority: dbEval.priority,
            rationale: dbEval.rationale,
            evaluationData: {
              overallImpact: dbEval.overall_impact,
              intensity: dbEval.intensity,
              duration: dbEval.duration,
              probability: dbEval.probability,
              frequencyOfOccurrence: dbEval.frequency_of_occurrence,
            },
            savedAt: dbEval.updated_at || dbEval.created_at
          };

          // Also update byRisk for direct lookups
          if (!state.byRisk[riskId]) {
            state.byRisk[riskId] = {};
          }
          state.byRisk[riskId].explorer = {
            priority: dbEval.priority,
            rationale: dbEval.rationale,
            riskArea
          };
        });
      })
      .addCase(fetchExplorerEvaluations.rejected, (state, action) => {
        const { assessmentUuid, error } = action.payload || action.meta.arg;
        if (state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid].explorerLoading = false;
          state.byAssessment[assessmentUuid].explorerError = error || action.error?.message;
        }
      });
  },
});

export const {
  // Builder (existing) actions
  setRiskEvaluation,
  updateRiskEvaluation,
  clearAssessmentEvaluations,
  clearRiskEvaluations,
  setLoading,
  setError,
  // Explorer-specific actions
  setExplorerRiskEvaluation,
  updateExplorerRiskEvaluation,
  clearExplorerEvaluations,
  clearExplorerAreaEvaluations,
} = riskEvaluationsSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectEvaluationsByRisk = (state) => state.riskEvaluations.byRisk;
export const selectEvaluationsByAssessment = (state) => state.riskEvaluations.byAssessment;

export const selectEvaluationForRisk = (state, riskId) =>
  state.riskEvaluations.byRisk[riskId] || {};

export const selectEvaluationForRiskAndTheme = (state, riskId, themeId) =>
  state.riskEvaluations.byRisk[riskId]?.[themeId] || {};

export const selectEvaluationsForSection = (state, assessmentUuid, sectionName) =>
  state.riskEvaluations.byAssessment[assessmentUuid]?.[sectionName] || {};

export const selectEvaluationsLoading = (state) => state.riskEvaluations.loading;
export const selectEvaluationsError = (state) => state.riskEvaluations.error;

/**
 * Get all evaluated risks for an assessment
 */
export const selectAllEvaluatedRisks = (state, assessmentUuid) => {
  const sections = state.riskEvaluations.byAssessment[assessmentUuid] || {};
  const allRisks = [];

  Object.keys(sections).forEach((sectionName) => {
    const categories = sections[sectionName] || {};
    Object.keys(categories).forEach((category) => {
      const risks = categories[category] || {};
      Object.keys(risks).forEach((riskId) => {
        allRisks.push({
          riskId,
          sectionName,
          category,
          ...risks[riskId],
        });
      });
    });
  });

  return allRisks;
};

/**
 * Count risks with evaluations for an assessment
 */
export const selectEvaluatedRisksCount = (state, assessmentUuid) =>
  selectAllEvaluatedRisks(state, assessmentUuid).length;

/**
 * Check if a risk has complete evaluation (all required attributes filled)
 */
export const selectIsRiskEvaluationComplete = (state, riskId, requiredAttributes = []) => {
  const evaluation = state.riskEvaluations.byRisk[riskId];
  if (!evaluation) return false;

  // Check if all required attributes have values across all themes
  return Object.values(evaluation).every((themeData) =>
    requiredAttributes.every((attr) => {
      const attrData = themeData[attr];
      return attrData && Object.values(attrData).some((v) => v !== '' && v !== null);
    })
  );
};

// ============================================================================
// Explorer-Specific Selectors
// ============================================================================

/**
 * Select all Explorer evaluations for an assessment
 */
export const selectExplorerEvaluations = (state, assessmentUuid) =>
  state.riskEvaluations.byAssessment[assessmentUuid]?.explorer || {};

/**
 * Select Explorer evaluations for a specific area
 */
export const selectExplorerAreaEvaluations = (state, assessmentUuid, areaKey) =>
  state.riskEvaluations.byAssessment[assessmentUuid]?.explorer?.[areaKey] || {};

/**
 * Select Explorer evaluation for a specific risk
 */
export const selectExplorerRiskEvaluation = (state, assessmentUuid, areaKey, category, riskId) =>
  state.riskEvaluations.byAssessment[assessmentUuid]?.explorer?.[areaKey]?.[category]?.[riskId] || null;

/**
 * Get all evaluated Explorer risks for an assessment
 */
export const selectAllExplorerEvaluatedRisks = (state, assessmentUuid) => {
  const explorer = state.riskEvaluations.byAssessment[assessmentUuid]?.explorer || {};
  const allRisks = [];

  Object.keys(explorer).forEach((areaKey) => {
    const categories = explorer[areaKey] || {};
    Object.keys(categories).forEach((category) => {
      const risks = categories[category] || {};
      Object.keys(risks).forEach((riskId) => {
        allRisks.push({
          riskId,
          areaKey,
          category,
          ...risks[riskId],
        });
      });
    });
  });

  return allRisks;
};

/**
 * Count Explorer risks with evaluations for an assessment
 */
export const selectExplorerEvaluatedRisksCount = (state, assessmentUuid) =>
  selectAllExplorerEvaluatedRisks(state, assessmentUuid).length;

/**
 * Select Explorer risk evaluation from byRisk (direct lookup)
 */
export const selectExplorerEvaluationByRisk = (state, riskId) =>
  state.riskEvaluations.byRisk[riskId]?.explorer || null;

/**
 * Check if an Explorer risk has a priority assigned
 */
export const selectExplorerRiskHasPriority = (state, assessmentUuid, areaKey, category, riskId) => {
  const evaluation = selectExplorerRiskEvaluation(state, assessmentUuid, areaKey, category, riskId);
  return !!evaluation?.priority;
};

/**
 * Get count of Explorer risks with priorities per area
 */
export const selectExplorerAreaPrioritizedCount = (state, assessmentUuid, areaKey) => {
  const areaEvals = selectExplorerAreaEvaluations(state, assessmentUuid, areaKey);
  let count = 0;

  Object.values(areaEvals).forEach((categoryRisks) => {
    Object.values(categoryRisks).forEach((riskEval) => {
      if (riskEval?.priority) count++;
    });
  });

  return count;
};

export default riskEvaluationsSlice.reducer;
